import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Partner, UsufructData } from 'types';

import { getPartnersByFieldsAndQuery } from 'modules/partners/api/getPartnersByFieldsAndQuery';
import { getUsufructData } from 'modules/partners/utils/getUsufructData';

import { ContributionData } from '../types/ContributionData';
import { createContributions } from '../utils/createContributions';

type UseGetContributionsProps = {
  societyId: string;
  totalAmount: number;
  societyActualSharesCount: number;
  page: number;
};

const fields = 'name sharesCount sharesBurdenCount';
const items = 10;

export const useGetContributions = ({
  societyId,
  totalAmount,
  societyActualSharesCount,
  page,
}: UseGetContributionsProps) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [usufructDataByPartner, setUsufructDataByPartner] = useState<{
    [key: string]: UsufructData[];
  }>({});
  const [contributions, setContributions] = useState<ContributionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPartners = useCallback(async () => {
    if (!societyId) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, {
          fields,
          items,
          page,
          onlyWithShares: true,
          includeUsufructuaries: true,
        })
      );

      if (!partners?.length) {
        setIsLoading(false);
        return;
      }

      const usufructDataByPartner = await getUsufructData(partners);
      setUsufructDataByPartner(usufructDataByPartner);

      setPartners(partners);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [page, societyId]);

  useEffect(() => {
    const contributions = createContributions({
      partners,
      usufructDataByPartner,
      societyActualSharesCount,
      totalAmount,
    });
    setContributions(contributions);
  }, [partners, societyActualSharesCount, totalAmount, usufructDataByPartner]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return {
    data: {
      contributions,
    },
    isLoading,
  };
};
